<template>
  <v-container class="mt-16">
    <v-alert type="error" v-if="isOldBrowser" class="mb-5" style="width: 100%">
      <br />
      Du verkar använda en föråldrad version av webbläsare! <br />
      Vi rekommenderar Google Chrome för bästa upplevelse.
      <br /><br />
    </v-alert>

    <h1 class="mt-10">{{ $t('vendors.dashboard.myProfile') }}</h1>

    <v-row>
      <v-dialog v-model="accountdialog" max-width="800px">
        <v-card>
          <v-card-title>
            <span class="headline">{{ $t('vendors.dashboard.editAccountInformation') }}</span>
          </v-card-title>

          <v-card-text>
            <v-container>
              <v-row>
                <v-col cols="12" md="4">
                  <v-text-field v-model="editedItem.name" :label="$t('vendors.dashboard.name')"></v-text-field>
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field v-model="editedItem.orgnr" :label="$t('vendors.dashboard.orgNumber')"></v-text-field>
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field v-model="editedItem.email" :label="$t('vendors.dashboard.email')"></v-text-field>
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field v-model="editedItem.address" :label="$t('vendors.dashboard.address')"></v-text-field>
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="editedItem.post_address"
                    :label="$t('vendors.dashboard.postAddress')"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field v-model="editedItem.phone" :label="$t('vendors.dashboard.phone')"></v-text-field>
                </v-col>
                <v-col cols="12">
                  <multi-country-selector-component
                    v-model="editedItem.countries"
                    :countries="lookupStore.countries"
                    :label="$t('vendors.dashboard.acceptedLanguages')"
                    :labelCountries="$t('components.common.countries')"
                    :labelAllCountries="$t('components.common.allCountries')"
                    :labelSelectAllCountries="$t('components.common.selectAllCountries')"
                    required
                  ></multi-country-selector-component>
                </v-col>
                <v-col cols="12">
                  <v-textarea
                    v-model="editedItem.description"
                    :label="$t('vendors.dashboard.description')"
                  ></v-textarea>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>

          <v-card-actions class="pr-4 pb-3">
            <v-spacer></v-spacer>
            <v-btn text @click="closeCompanyEditDialog">{{ $t('common.cancel') }}</v-btn>
            <v-btn
              color="primary"
              class="black--text"
              @click="saveCompanyInformation"
              :loading="savingCompanyData"
              :disabled="!valid"
              >{{ $t('common.save') }}</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-col cols="12" class="my-3">
        <v-card>
          <v-container>
            <v-row>
              <v-col cols="12">
                <p v-html="$t('vendors.dashboard.accountInfoText')"></p>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" class="mt-3">
                <v-btn class="mr-5 black--text" color="primary" :to="{ name: 'Upload' }">
                  <v-icon class="mr-2">mdi-cloud-upload</v-icon>{{ $t('vendors.dashboard.uploadImages') }}
                </v-btn>
                <v-btn color="primary" class="black--text" :to="{ name: 'List' }">
                  <v-icon class="mr-2">mdi-image-multiple</v-icon>{{ $t('vendors.dashboard.imageAdministration') }}
                </v-btn>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-col>

      <!-- Company profile -->
      <v-col cols="12" lg="6" class="my-3">
        <v-card>
          <v-container>
            <v-row>
              <v-col>
                <h3>{{ $t('vendors.dashboard.companyAccountInfoTitle') }}</h3>
              </v-col>
              <v-col class="d-flex justify-end">
                <a href @click.prevent="openCompanyEditDialog">{{ $t('vendors.dashboard.editInformation') }}</a>
              </v-col>
            </v-row>

            <v-row row>
              <v-col cols="12">
                <p class="mb-1">{{ $t('vendors.dashboard.company') }}: {{ userStore.selectedCompany.name }}</p>
                <p class="mb-1">{{ $t('vendors.dashboard.orgNumber') }}: {{ userStore.selectedCompany.orgnr }}</p>
                <p class="mb-1">
                  {{ $t('vendors.dashboard.address') }}: {{ userStore.selectedCompany.address }},
                  {{ userStore.selectedCompany.post_address }}
                </p>
                <p class="mb-1">{{ $t('vendors.dashboard.phone') }}: {{ userStore.selectedCompany.phone }}</p>
                <p class="mb-1">{{ $t('vendors.dashboard.email') }}: {{ userStore.selectedCompany.email }}</p>
              </v-col>
              <v-col cols="12" class="pb-0">
                <p class="mb-0 font-weight-bold">{{ $t('vendors.dashboard.acceptedLanguages') }}</p>
                <p class="mb-0 subtitle-2">{{ $t('vendors.dashboard.youCanChangeThis') }}</p>
              </v-col>
              <v-col cols="12">
                <p class="mb-1">{{ companyCountriesAsString }}</p>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-col>

      <!-- User profile -->
      <v-col cols="12" lg="6" class="my-3">
        <v-card class="fill-height">
          <v-container>
            <v-row>
              <v-col>
                <h3>{{ $t('vendors.dashboard.userAccountInfoTitle') }}</h3>
              </v-col>
              <v-col class="d-flex justify-end">
                <a href @click.prevent="editUserDialog">{{ $t('vendors.dashboard.editInformation') }}</a>
              </v-col>
            </v-row>

            <v-row row>
              <v-col cols="12">
                <p class="mb-1">{{ $t('vendors.dashboard.name') }}: {{ userStore.user.name }}</p>
                <p class="mb-1">{{ $t('vendors.dashboard.email') }}: {{ userStore.user.email }}</p>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-col>

      <v-col cols="4" class="my-3">
        <v-card>
          <v-card-title>Antal synliga bilder</v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="12">
                <p class="font-weight-bold text-h3">{{ stats.visibleApproved }}</p>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="4" class="my-3">
        <v-card>
          <v-card-title>Antal godkända bilder</v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="12">
                <p class="font-weight-bold text-h3">{{ stats.allApproved }}</p>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="4" class="my-3">
        <v-card>
          <v-card-title>Totalt antal bilder</v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="12">
                <p class="font-weight-bold text-h3">{{ stats.total }}</p>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" class="my-3">
        <!-- <v-container> -->
        <v-row row>
          <v-col cols="12" md="6" lg="4" class="pr-2">
            <v-card class="fill-height">
              <v-card-title>{{ $t('vendors.dashboard.downloadTop3') }}</v-card-title>
              <v-card-text>
                <v-container fluid>
                  <v-row>
                    <v-col v-for="image in stats.top" :key="image.id" class="d-flex child-flex" cols="4">
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                          <v-card flat tile class="d-flex" v-on="on">
                            <v-img :src="getImagePath(image)" aspect-ratio="1" class="grey lighten-2">
                              <template v-slot:placeholder>
                                <v-row class="fill-height ma-0" align="center" justify="center">
                                  <v-progress-circular indeterminate color="grey lighten-5"></v-progress-circular>
                                </v-row>
                              </template>
                            </v-img>
                          </v-card>
                        </template>
                        <span>{{ image.download_count }} {{ $t('vendors.dashboard.downloads') }}</span>
                      </v-tooltip>
                    </v-col>
                  </v-row>
                </v-container>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" md="6" lg="4" class="px-1">
            <v-card class="fill-height">
              <v-card-title>{{ $t('vendors.dashboard.imagesForReview') }}</v-card-title>
              <v-card-text>
                <v-container fluid>
                  <v-row>
                    <v-col v-for="image in stats.review" :key="image.id" class="d-flex child-flex" cols="4">
                      <v-card flat tile class="d-flex">
                        <v-img :src="getImagePath(image)" aspect-ratio="1" class="grey lighten-2">
                          <template v-slot:placeholder>
                            <v-row class="fill-height ma-0" align="center" justify="center">
                              <v-progress-circular indeterminate color="grey lighten-5"></v-progress-circular>
                            </v-row>
                          </template>
                        </v-img>
                      </v-card>
                    </v-col>
                  </v-row>
                </v-container>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" md="6" lg="4" class="pl-2">
            <v-card class="fill-height">
              <v-card-title>{{ $t('vendors.dashboard.rejectedImages') }}</v-card-title>
              <v-card-text>
                <v-container fluid>
                  <v-row>
                    <v-col v-for="image in stats.rejected" :key="image.id" class="d-flex child-flex" cols="4">
                      <v-card flat tile class="d-flex">
                        <v-img :src="getImagePath(image)" aspect-ratio="1" class="grey lighten-2">
                          <template v-slot:placeholder>
                            <v-row class="fill-height ma-0" align="center" justify="center">
                              <v-progress-circular indeterminate color="grey lighten-5"></v-progress-circular>
                            </v-row>
                          </template>
                        </v-img>
                      </v-card>
                    </v-col>
                  </v-row>
                </v-container>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
        <!-- </v-container> -->
      </v-col>
    </v-row>

    <user-edit-dialog-component :visible="showUserEditDialog" :useCountries="false" @close="editUserClosed()"></user-edit-dialog-component>
  </v-container>
</template>

<script>
import { UserService } from '../../services/user.service';
import { VendorService } from '../../services/vendor.service';
import { mapStores } from 'pinia';
import { useUserStore } from '../../stores/UserStore';
import { useLookupStore } from '../../stores/LookupStore';
import MultiCountrySelectorComponent from '@shared/components/MultiCountrySelectorComponent.vue';
import UserEditDialogComponent from '../../components/dialog/UserEditDialogComponent.vue';

export default {
  components: {
    MultiCountrySelectorComponent,
    UserEditDialogComponent,
  },

  data() {
    return {
      accountdialog: false,
      savingCompanyData: false,
      showUserEditDialog: false,
      editedItem: {},
      stats: {},
    };
  },

  computed: {
    ...mapStores(useUserStore, useLookupStore),

    isOldBrowser() {
      return navigator.userAgent.indexOf('MSIE') !== -1 || navigator.appVersion.indexOf('Trident/') > -1;
    },

    valid() {
      return this.editedItem.name && this.editedItem.email && this.editedItem.countries?.length > 0;
    },

    companyCountriesAsString() {
      if (this.userStore.selectedCompany?.countries.length === this.lookupStore.countries.length) {
        return this.$t('components.common.allCountries');
      }
      return (this.userStore.selectedCompany?.countries || []).map((country) => country.name).join(', ');
    },

    userCountriesAsString() {
      if (this.userStore.user?.countries?.length === this.lookupStore.countries.length) {
        return this.$t('components.common.allCountries');
      }
      return (this.userStore.user?.countries || []).map((country) => country.name).join(', ');
    },
  },

  async created() {
    await this.loadStatistics();
    this.lookupStore.loadCountries();

    this.userStore.$subscribe(async () => {
      if (this.userStore.selectedCompany) {
        await this.loadStatistics();
      }
    });
  },

  methods: {
    async loadStatistics() {
      const userService = new UserService();
      this.stats = await userService.getVendorStatistics();
    },

    getImagePath(image) {
      return image.path + '/' + image.thumb_name;
    },

    openCompanyEditDialog() {
      this.editedItem = Object.assign({}, this.userStore.selectedCompany);
      this.accountdialog = true;
    },

    closeCompanyEditDialog() {
      this.editedItem = {};
      this.accountdialog = false;
      this.savingCompanyData = false;
    },

    editUserDialog() {
      this.showUserEditDialog = true;
    },

    editUserClosed() {
      this.showUserEditDialog = false;
    },

    saveCompanyInformation() {
      this.savingCompanyData = true;
      new VendorService()
        .updateVendorInformation(this.editedItem)
        .then((data) => {
          this.userStore.selectedCompany = data;
          this.closeCompanyEditDialog();
        })
        .catch((error) => {
          console.error('Error while updating account information', error);
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.btn i.icon {
  padding-right: 5px;
}
</style>
